
import { useState } from 'react'
import { X, Check, Gift } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card'
import { getPricingData } from 'shared-web/data/pricing'

export default function PricingCards() {
  const [isAnnual, setIsAnnual] = useState(true)
  const pricingData = getPricingData(isAnnual)

  const ICON_MAP: Record<string, React.ReactNode> = {
    'G': <Gift className="h-5 w-5 text-green-500 mr-2 shrink-0" />,
    '-': <X className="h-5 w-5 text-orange-500 mr-2 shrink-0" />
  }

  return (
    <div className="space-y-8 py-4">
      {/* Billing Toggle */}
      <div className="flex justify-center">
        <div className="bg-gray-100 p-1 rounded-lg flex items-center space-x-1">
          <button
            onClick={() => setIsAnnual(false)}
            className={`px-4 py-2 text-sm font-medium rounded-md cursor-pointer transition-all duration-200 ${
              !isAnnual 
                ? 'bg-white text-gray-900 shadow-sm' 
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Monthly
          </button>
          <button
            onClick={() => setIsAnnual(true)}
            className={`px-4 py-2 text-sm font-medium rounded-md cursor-pointer transition-all duration-200 flex items-center space-x-2 ${
              isAnnual 
                ? 'bg-white text-gray-900 shadow-sm' 
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <span>Annual</span>
            <span className="bg-green-100 text-green-800 text-xs font-semibold px-2 py-1 rounded-full">
              Save 20%
            </span>
          </button>
        </div>
      </div>

      {/* Pricing Cards */}
      <div className={isAnnual ? "grid md:grid-cols-2 lg:grid-cols-4 gap-6" : "grid md:grid-cols-2 lg:grid-cols-3 gap-6"}>
        {pricingData.map((plan, index) => (
          (plan.monthlyPrice === null && !isAnnual) ? null :
          <Card
            key={index}
            className={`flex flex-col ${plan.popular ? 'border-purple-400 shadow-lg relative overflow-hidden' : 'border-gray-200'}`}>
            {plan.popular && (
              <div className="absolute top-0 right-0">
                <div className="bg-purple-600 text-white text-xs font-semibold px-3 py-1 rounded-bl-lg">Popular</div>
              </div>
            )}
            <CardHeader>
              <CardTitle>{plan.title}</CardTitle>
              <div className="flex items-baseline mt-2">
                <span className="text-3xl font-bold">
                  ${isAnnual ? plan.annualPrice : plan.monthlyPrice}
                </span>
                {plan.title !== 'Free' && (
                  <span className="text-gray-500 ml-1">
                    /month
                  </span>
                )}
              </div>
              {isAnnual && plan.title !== 'Free' && (
                <div className="text-sm text-gray-500">
                  <span className="">Billed ${plan.annualPrice * 12}/year</span>
                </div>
              )}
              <CardDescription className="mt-2">{plan.description}</CardDescription>
            </CardHeader>
            <CardContent className="flex-grow">
              <ul className="space-y-3">
                {plan.features.map((feature, i) => (
                  <li key={i} className="flex items-start">
                    {ICON_MAP[feature[0]] ?? <Check className="h-5 w-5 text-green-500 mr-2 shrink-0" />}
                    <span className={feature[0] === 'G' ? "text-gray-600 font-semibold" : "text-gray-600"}>{feature.slice(2)}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
            <CardFooter>
              <Button className={`w-full ${plan.popular ? 'bg-purple-600 hover:bg-purple-700' : 'bg-gray-800 hover:bg-gray-900'}`}>
                {plan.cta}
              </Button>
            </CardFooter>
          </Card>
        ))}
      </div>
    </div>
  )
}
